import { shopItemsUrl, shopPageQuery, type Page, type PublicShopItem } from "@fusiqo/vozqi-shared";
import { useCallback, useEffect, useRef, useState } from "react";

export type ServicesState = {
  items: PublicShopItem[];
  /** Which page listed each service, by token: its photo is vouched for by that page. */
  pageOf: Record<string, string>;
  status: "loading" | "idle" | "failed";
  /** Where the next page starts; null on the last. */
  cursor: string | null;
  /** More to ask for: «Ver más servicios». */
  more: boolean;
  /** The first page answered, well or not: what lies below may take its place. */
  settled: boolean;
  loadMore: () => void;
  retry: () => void;
};

/**
 * The profile's services, a page at a time (`kind=SERVICE`). No infinite
 * scroll: the products come after them, so the visitor asks for more.
 * Nothing is asked of a profile that shares no service, nor of one coming
 * back from an item with its services `restored`.
 */
export function useServices(
  slug: string,
  enabled: boolean,
  restored: { items: PublicShopItem[]; cursor: string | null; pageOf: Record<string, string> } | null,
): ServicesState {
  const [items, setItems] = useState<PublicShopItem[]>(restored?.items ?? []);
  const [pageOf, setPageOf] = useState<Record<string, string>>(restored?.pageOf ?? {});
  const [nextCursor, setNextCursor] = useState<string | null>(restored?.cursor ?? null);
  const [status, setStatus] = useState<ServicesState["status"]>(enabled && restored === null ? "loading" : "idle");
  const [settled, setSettled] = useState(!enabled || restored !== null);
  /** The cursor the last request asked from: what «Reintentar» asks again. */
  const lastCursor = useRef<string | null>(null);

  const fetchPage = useCallback(
    async (cursor: string | null, signal?: AbortSignal) => {
      lastCursor.current = cursor;
      setStatus("loading");
      try {
        const response = await fetch(shopItemsUrl(slug, cursor, "", null, "SERVICE"), { signal: signal ?? null });
        if (!response.ok) throw new Error(String(response.status));
        const page = (await response.json()) as Page<PublicShopItem>;
        const listedBy = shopPageQuery(cursor, "", null, "SERVICE");
        setItems((before) => (cursor === null ? page.items : [...before, ...page.items]));
        setPageOf((before) => ({
          ...(cursor === null ? {} : before),
          ...Object.fromEntries(page.items.map((item) => [item.token, listedBy])),
        }));
        setNextCursor(page.nextCursor);
        setStatus("idle");
      } catch (error) {
        if (signal?.aborted === true) return;
        console.error("[profile] services failed", error);
        setStatus("failed");
      }
      setSettled(true);
    },
    [slug],
  );

  useEffect(() => {
    if (!enabled || restored !== null) return;
    const controller = new AbortController();
    void fetchPage(null, controller.signal);
    return () => controller.abort();
  }, [fetchPage, enabled, restored]);

  return {
    items,
    pageOf,
    status,
    cursor: nextCursor,
    more: nextCursor !== null && status === "idle",
    settled,
    loadMore: () => void fetchPage(nextCursor),
    retry: () => void fetchPage(lastCursor.current),
  };
}
